import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { IconRegularClose, IconRegularChevronLeft } from '@notino/react-styleguide'
import { EventListeners } from 'overlayscrollbars'

// styles
import * as SC from './ReservationBookingModalLayoutStyles'

// hooks
import useMessages from '../../../hooks/useMessages'

// utils
import { SCROLLBAR_OPTIONS } from '../../../utils/helper'

/** Stable id for the booking modal step title — used as Modal `ariaLabelledBy`. */
export const RESERVATION_BOOKING_MODAL_TITLE_ID = 'reservation-booking-modal-title'

type Props = PropsWithChildren<{
	onClose: () => void
	onBackButtonClick?: () => void
	title?: React.ReactNode
	headerImage?: string
	headerContent?: React.ReactNode
	headerContentVisibility?: ['mobile' | null, 'desktop' | null]
	footerContent?: React.ReactNode
	/** When false, skip focusing the step title on mount. Default true. */
	autoFocusTitle?: boolean
}>

const ReservationBookingModalLayout = (props: Props) => {
	const {
		onClose,
		onBackButtonClick,
		title,
		footerContent,
		headerImage,
		headerContent,
		headerContentVisibility = ['mobile', 'desktop'],
		autoFocusTitle = true,
		children
	} = props
	const { messages } = useMessages()
	const titleRef = useRef<HTMLHeadingElement>(null)

	const [isScrolled, setIsScrolled] = useState(false)

	useEffect(() => {
		if (!autoFocusTitle) {
			return
		}

		titleRef.current?.focus()
	}, [autoFocusTitle])

	const handleScroll: EventListeners['scroll'] = (instance) => {
		const scrollElement = instance.elements().viewport
		const { scrollTop } = scrollElement

		const newScrolledState = scrollTop !== 0

		if (newScrolledState !== isScrolled) {
			setIsScrolled(newScrolledState)
		}
	}

	const renderHeaderContentOnMobile = headerContentVisibility[0] === 'mobile'
	const renderHeaderContentOnDesktop = headerContentVisibility[1] === 'desktop'

	return (
		<SC.LayoutWrapper>
			<SC.LayoutAside $backgroundImage={headerImage} />
			<SC.LayoutMain>
				<SC.ScrollableContent
					options={SCROLLBAR_OPTIONS}
					events={{
						scroll: handleScroll
					}}
				>
					<SC.FixedHeader $isScrolled={isScrolled}>
						<SC.FixedHeaderContent>
							{onBackButtonClick && (
								<SC.BackButton type={'button'} onClick={onBackButtonClick} aria-label={messages['Go back']}>
									<IconRegularChevronLeft color={isScrolled ? 'icon.primary' : 'icon.inverse'} aria-hidden />
								</SC.BackButton>
							)}
							<SC.HeaderTitleWrapper>
								<SC.HeaderTitle ref={titleRef} id={RESERVATION_BOOKING_MODAL_TITLE_ID} tabIndex={-1}>
									{title}
								</SC.HeaderTitle>
								{headerContent && renderHeaderContentOnDesktop && <SC.HeaderContentDesktop>{headerContent}</SC.HeaderContentDesktop>}
							</SC.HeaderTitleWrapper>
							<SC.CloseButton type={'button'} onClick={() => onClose()} aria-label={messages['Close modal']}>
								<IconRegularClose color={isScrolled ? 'icon.primary' : 'icon.inverse'} aria-hidden />
							</SC.CloseButton>
						</SC.FixedHeaderContent>
					</SC.FixedHeader>
					<SC.LayoutHeader $backgroundImage={headerImage}>
						{headerContent && renderHeaderContentOnMobile && <SC.HeaderContent>{headerContent}</SC.HeaderContent>}
					</SC.LayoutHeader>
					<SC.LayoutContent>{children}</SC.LayoutContent>
				</SC.ScrollableContent>
				{footerContent && <SC.LayoutFooter>{footerContent}</SC.LayoutFooter>}
			</SC.LayoutMain>
		</SC.LayoutWrapper>
	)
}

export default ReservationBookingModalLayout
